import { useEffect, useState } from "react";
import apiHelper from "../../../utils/apiHelper";
import CustomSwitchToggle from "../../common/Switch";
import SimpleSpinner from "../../loader";
import {
  getMyNotificationSettings,
  updateMyNotificationSettings,
  NotificationGroup,
} from "../../../services/notificationSettings.service";
import "./NotificationSettings.css";

const NotificationSettingsComponent = () => {
  const [groups, setGroups] = useState<NotificationGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const loadSettings = async () => {
    setLoading(true);
    const res = await apiHelper(getMyNotificationSettings, { showNotification: false });
    if (res?.data?.success?.groups) {
      setGroups(res.data.success.groups);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const toggleItem = (groupKey: string, itemKey: string) => {
    setGroups((prev) =>
      prev.map((g) =>
        g.groupKey !== groupKey
          ? g
          : {
              ...g,
              items: g.items.map((i) =>
                i.key === itemKey ? { ...i, enabled: !i.enabled } : i
              ),
            }
      )
    );
    setDirty(true);
  };

  const onSave = async () => {
    setSaving(true);
    const preferences = groups.flatMap((g) =>
      g.items.map((i) => ({ key: i.key, enabled: i.enabled }))
    );
    const res = await apiHelper(
      updateMyNotificationSettings,
      {
        showNotification: true,
        title: "Notification Settings",
        successMessage: "Your notification preferences have been saved.",
      },
      preferences
    );
    if (res?.data?.success?.groups) {
      setGroups(res.data.success.groups);
      setDirty(false);
    }
    setSaving(false);
  };

  return (
    <div className="notificationSettingsContainer">
      <div className="notificationSettingsHeader">
        <div>
          <h2 className="page-title">Notification Settings</h2>
          <p className="notificationSettingsSubtext">
            Choose which email notifications you want to receive. All notifications are on by default.
          </p>
        </div>
        <button
          type="button"
          className="notificationSaveBtn clickable"
          onClick={onSave}
          disabled={saving || !dirty}
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      {loading ? (
        <div className="notificationSettingsLoadingContainer">
          <SimpleSpinner isFullPage />
        </div>
      ) : groups.length === 0 ? (
        <p className="notificationSettingsSubtext mt-4">
          There are no notification settings available for your role.
        </p>
      ) : (
        <div className="notificationGroupsWrapper">
          {groups.map((group) => (
            <div className="notification-card" key={group.groupKey}>
              <div className="notification-card-header">
                <h5 className="section-title black m-0">{group.groupLabel}</h5>
              </div>
              <div className="notification-items">
                {group.items.map((item) => (
                  <div className="notification-item-row" key={item.key}>
                    <div className="notification-item-text">
                      <p className="normal_text fw-500 black m-0">{item.label}</p>
                      {item.description && (
                        <p className="small_text grey_subtext1 m-0">{item.description}</p>
                      )}
                    </div>
                    <CustomSwitchToggle
                      checked={item.enabled}
                      onClick={() => toggleItem(group.groupKey, item.key)}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationSettingsComponent;
